import { parseIsoDate, toIsoDate } from "./dates";

export const XP = {
  set: 10,
  warmupSet: 2,
  workout: 50,
  pr: 25,
  weeklyGoal: 100,
  achievement: 50,
  cardioMinute: 2,
} as const;

/** Total XP needed to reach `level` (level 1 = 0 XP). */
export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  return Math.floor(100 * Math.pow(level - 1, 1.6));
}

export function levelFromXp(xp: number) {
  let level = 1;
  while (xpForLevel(level + 1) <= xp) level++;
  const current = xpForLevel(level);
  const next = xpForLevel(level + 1);
  return {
    level,
    xpIntoLevel: xp - current,
    xpForNext: next - current,
    progress: (xp - current) / (next - current),
  };
}

export const RANKS = [
  { minLevel: 1, key: "rookie" },
  { minLevel: 5, key: "regular" },
  { minLevel: 10, key: "athlete" },
  { minLevel: 15, key: "iron" },
  { minLevel: 20, key: "beast" },
  { minLevel: 30, key: "legend" },
] as const;

export function rankForLevel(level: number) {
  return [...RANKS].reverse().find((r) => level >= r.minLevel)!.key;
}

export type UserStats = {
  workouts: number;
  cardio: number;
  prs: number;
  volumeKg: number;
  weekStreak: number;
  level: number;
  /** Local hour the latest workout started (for early-bird / night-owl). */
  lastStartHour: number | null;
};

export const ACHIEVEMENTS: {
  key: string;
  icon: string;
  check: (s: UserStats) => boolean;
}[] = [
  { key: "first_workout", icon: "🎉", check: (s) => s.workouts >= 1 },
  { key: "workouts_5", icon: "🔥", check: (s) => s.workouts >= 5 },
  { key: "workouts_12", icon: "📅", check: (s) => s.workouts >= 12 },
  { key: "workouts_25", icon: "💪", check: (s) => s.workouts >= 25 },
  { key: "workouts_50", icon: "🏆", check: (s) => s.workouts >= 50 },
  { key: "workouts_100", icon: "👑", check: (s) => s.workouts >= 100 },
  { key: "first_pr", icon: "📈", check: (s) => s.prs >= 1 },
  { key: "prs_10", icon: "🚀", check: (s) => s.prs >= 10 },
  { key: "prs_50", icon: "⚡", check: (s) => s.prs >= 50 },
  { key: "streak_2", icon: "✅", check: (s) => s.weekStreak >= 2 },
  { key: "streak_4", icon: "🗓️", check: (s) => s.weekStreak >= 4 },
  { key: "streak_12", icon: "💎", check: (s) => s.weekStreak >= 12 },
  { key: "volume_10t", icon: "🏋️", check: (s) => s.volumeKg >= 10_000 },
  { key: "volume_100t", icon: "🦍", check: (s) => s.volumeKg >= 100_000 },
  { key: "first_cardio", icon: "🏃", check: (s) => s.cardio >= 1 },
  { key: "cardio_10", icon: "🫀", check: (s) => s.cardio >= 10 },
  { key: "level_5", icon: "⭐", check: (s) => s.level >= 5 },
  { key: "level_10", icon: "🌟", check: (s) => s.level >= 10 },
  { key: "level_20", icon: "✨", check: (s) => s.level >= 20 },
  {
    key: "early_bird",
    icon: "🌅",
    check: (s) => s.lastStartHour != null && s.lastStartHour < 7,
  },
  {
    key: "night_owl",
    icon: "🦉",
    check: (s) => s.lastStartHour != null && s.lastStartHour >= 22,
  },
];

export function achievementIcon(key: string) {
  return ACHIEVEMENTS.find((a) => a.key === key)?.icon ?? "🏅";
}

/**
 * Consecutive weeks (ending this week or last week) in which the weekly goal
 * was met. The running week doesn't break the streak until it's over.
 */
export function weekStreak(sessionWeekKeys: string[], goal: number, currentWeekKey: string): number {
  const counts = new Map<string, number>();
  for (const k of sessionWeekKeys) counts.set(k, (counts.get(k) ?? 0) + 1);
  const met = (k: string) => (counts.get(k) ?? 0) >= goal;

  let streak = met(currentWeekKey) ? 1 : 0;
  const cursor = parseIsoDate(currentWeekKey);
  for (;;) {
    cursor.setDate(cursor.getDate() - 7);
    if (!met(toIsoDate(cursor))) break;
    streak++;
  }
  return streak;
}
